<script setup>
import { computed, defineComponent } from 'vue';
import {
  BaseBadge,
  BaseButton,
  BaseIcon,
  BaseTooltip,
} from '@/components/Base';
import ApplicantCard from '@/components/Elements/Applicant/ApplicantCard.vue';
import PercentageData from '@/components/Elements/Scaffold/PercentageData.vue';
import TextData from '@/components/Elements/Scaffold/TextData.vue';
import englishAssessmentUtils from '@/utils/englishAssessmentUtils';
import { vElementHover } from '@vueuse/components';
import { STATUS_MAP, INTEGRITY_RISK_STATUS_MAP } from '@/constants/assessments';

defineComponent({
  directives: {
    elementHover: vElementHover,
  },
});

/**
 * @typedef {Object} Assessment
 * @property {number} id - Assessment ID
 * @property {'codility'|'smallTalk2Me'} type - Assessment type
 * @property {string} [link] - Link for Codility test
 * @property {string} [test_link] - Link for English assessment
 * @property {string} [report_url] - Report URL for English assessment
 * @property {Object} [test] - Test details for Codility
 * @property {Object} [result] - Result details for Codility
 * @property {string} [status] - Status for English assessment
 * @property {string} [score] - Score for English assessment
 * @property {Object} [detailed_scores] - Detailed scores for English assessment
 * @property {string} [started_at] - Start date for English assessment
 * @property {string} [finished_at] - Finish date for English assessment
 */

const props = defineProps({
  assessment: {
    type: Object,
    required: true,
  },
  isCompleted: {
    type: Boolean,
    default: false,
  },
  isRefreshing: {
    type: Boolean,
    default: false,
  },
  hoveredTask: {
    type: String,
    default: null,
  },
  isCombinedTechnicalChallenge: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['refresh', 'download', 'copy', 'hover']);

const isCodility = computed(() => props.assessment.type === 'codility');
const isEnglishAssessment = computed(
  () => props.assessment.type === 'smallTalk2Me'
);
const cardTitle = computed(() => {
  if (isCodility.value) {
    return props.assessment.test?.name ?? 'Technical Assessment';
  }
  return 'English Assessment';
});

const passingThreshold = computed(() => {
  if (!isCodility.value) return null;
  return `${props.assessment.result?.passing_threshold ?? props.assessment.passing_threshold}%`;
});

const totalScore = computed(() => {
  if (!isCodility.value) return null;
  if (props.isCompleted) {
    return Math.round(props.assessment.result?.result_value);
  }
  return getPercentageDataValue(
    props.assessment.result?.result,
    props.assessment.result?.max_result
  );
});

const detailedScoreItems = computed(() => {
  if (!isEnglishAssessment.value) return [];
  return englishAssessmentUtils.getOrderedScoreDetails(
    props.assessment.detailed_scores
  );
});

const showCombinedTechnicalChallengeWarning = computed(
  () =>
    isCodility.value &&
    props.isCombinedTechnicalChallenge &&
    ['created', 'canceled'].includes(props.assessment.status)
);

const statusInfo = computed(() => {
  return STATUS_MAP[props.assessment.status];
});

function getPercentageDataValue(result, maxResult) {
  return result != null ? (result / maxResult) * 100 : null;
}

const integrityRiskLevel = computed(() => {
  return INTEGRITY_RISK_STATUS_MAP[
    props.assessment?.result?.risk_status ?? 'no_risk'
  ];
});

function onHover(taskName, state) {
  emit('hover', props.assessment.id, taskName, state);
}

function handleCopyLink() {
  if (isCodility.value) {
    emit('copy', props.assessment.link);
  } else {
    emit('copy', props.assessment.test_link);
  }
}

function handleRefresh() {
  emit('refresh', props.assessment);
}

function handleDownload() {
  emit('download', props.assessment);
}

function handleOpenReport() {
  englishAssessmentUtils.openReport(props.assessment.report_url);
}
</script>

<template>
  <ApplicantCard :title="cardTitle">
    <template #title>
      <div class="flex justify-between">
        <div class="flex flex-col gap-2">
          <span class="whitespace-normal">{{ cardTitle }}</span>
          <div class="flex gap-2 items-center">
            <BaseBadge
              class="capitalize !px-0"
              size="sm"
              color="transparent"
              data-testid="assessment-type-badge"
            >
              {{ assessment.type }}
            </BaseBadge>
            <BaseBadge
              size="xs"
              :color="statusInfo?.color"
              class="!px-1.5"
              data-testid="assessment-status-badge"
            >
              {{ statusInfo?.label }}
              <template #append>
                <BaseIcon
                  v-if="
                    statusInfo?.iconColor &&
                    props.assessment.status === 'failed'
                  "
                  name="info-circle"
                  :color="statusInfo?.iconColor"
                />
                <BaseTooltip
                  v-if="props.assessment.status === 'failed'"
                  max-width="216px"
                  :offset="[0, 10]"
                >
                  <div>
                    There was an issue running this test. Please contact the
                    Talent Team for support.
                  </div>
                </BaseTooltip>
              </template>
            </BaseBadge>
          </div>
        </div>
        <div v-if="isCodility && isCompleted">
          <BaseButton
            icon-prepend="refresh-ccw-04"
            icon-size="20px"
            round
            class="icon-secondary-gray cursor-pointer"
            data-testid="refresh-button"
            outlined
            :disable="isRefreshing"
            @click="handleRefresh"
          ></BaseButton>
          <BaseTooltip :offset="[0, 6]">Update results</BaseTooltip>
        </div>
      </div>
    </template>

    <div class="flex flex-col gap-3">
      <template v-if="isCodility">
        <BaseBadge
          v-if="isCompleted && assessment.result?.risk_status"
          size="xs"
          :color="integrityRiskLevel.color"
          class="!px-1.5"
          data-testid="assessment-result-risk-badge"
        >
          Integrity risk: {{ integrityRiskLevel.label }}
        </BaseBadge>
        <PercentageData
          title="Passing Threshold"
          :value="passingThreshold"
        />
        <PercentageData
          v-if="isCompleted || assessment.result?.result"
          title="Total"
          :value="totalScore"
        ></PercentageData>

        <template v-if="assessment.result">
          <div
            v-for="task in assessment.result.tasks"
            :key="`${assessment.id}-task-${task.task_name}`"
            v-element-hover="(state) => onHover(task.name, state)"
            class="flex flex-col"
            :class="{ 'gap-y-4': !isCompleted, 'gap-y-2': isCompleted }"
          >
            <PercentageData
              :title="task.task_name"
              :value="
                getPercentageDataValue(task.result, task.max_result) ??
                'Not submitted'
              "
              :underline="hoveredTask === assessment.id + task.name"
            ></PercentageData>
            <!-- Add visible task language for test to find -->
            <div
              v-if="task.prg_lang"
              class="sr-only"
            >
              {{ task.prg_lang }}
            </div>
            <BaseTooltip
              tooltip-class="border border-proxify-gray-200 gap-[12px]"
            >
              <div class="task-card">
                <div :class="{ 'flex flex-col gap-2': isCompleted }">
                  <div class="text-body-sm font-semibold">
                    {{ task.task_name }}
                  </div>
                  <TextData
                    v-if="task.prg_lang"
                    :class="{ 'mt-2': !isCompleted }"
                    title="Language"
                    :value="task.prg_lang"
                  />
                  <PercentageData
                    title="Score"
                    :value="
                      getPercentageDataValue(task.result, task.max_result) ??
                      'Not submitted'
                    "
                    :class="{ 'mt-2': !isCompleted }"
                  ></PercentageData>
                </div>
              </div>
            </BaseTooltip>
          </div>
        </template>
        <p
          v-if="showCombinedTechnicalChallengeWarning"
          class="whitespace-normal text-body-sm font-medium text-proxify-error-500"
          data-testid="combined-technical-challenge-warning"
        >
          This is a
          <span class="font-bold">Combined Technical Assessment.</span>
          Don’t share the test link with the candidate.
          {{
            assessment.status === 'canceled' ? 'Create a new test instead.' : ''
          }}
        </p>
      </template>

      <template v-else-if="isEnglishAssessment">
        <template v-if="isCompleted">
          <TextData
            title="Passing level"
            :value="assessment.cefr_level_threshold"
          ></TextData>
          <TextData
            title="Level"
            :value="assessment.score"
          ></TextData>
          <div class="sr-only">{{ assessment.score }}</div>
          <div
            v-for="(item, index) in detailedScoreItems"
            :key="`${assessment.started_at}-score-${index}`"
            class="flex flex-col gap-y-2"
          >
            <TextData
              :title="item.label"
              :value="item.value"
              :value-class="{
                '!text-proxify-success-500': item.value.charAt(0) === 'C',
                '!text-proxify-warning-500': item.value.charAt(0) === 'B',
                '!text-proxify-error-500': item.value.charAt(0) === 'A',
              }"
            ></TextData>
            <div class="sr-only">{{ item.label }}: {{ item.value }}</div>
          </div>
        </template>
        <template v-else>
          <TextData
            title="Passing level"
            :value="assessment.cefr_level_threshold"
            value-class="!text-proxify-gray-400"
          ></TextData>
        </template>
      </template>
    </div>

    <div class="border-t border-proxify-gray-200 mt-4 -mb-3"></div>

    <template #footer>
      <div
        class="w-full pt-3 pb-1"
        :class="{
          'flex justify-between':
            isCodility || (isEnglishAssessment && !isCompleted),
          'flex justify-center': isEnglishAssessment && isCompleted,
        }"
      >
        <template v-if="isCodility">
          <BaseButton
            class="font-semibold"
            :class="
              isCompleted ? 'text-proxify-primary' : 'text-proxify-gray-400'
            "
            icon-prepend="download-02"
            icon-size="20px"
            :disable="!isCompleted"
            padding="0"
            data-testid="download-button"
            @click="handleDownload"
          >
            Download PDF
          </BaseButton>
          <BaseButton
            class="text-proxify-primary font-semibold"
            icon-prepend="copy-03"
            icon-size="20px"
            padding="0"
            data-testid="copy-link-button"
            @click="handleCopyLink"
          >
            Copy link
          </BaseButton>
        </template>

        <template v-else-if="isEnglishAssessment">
          <template v-if="isCompleted">
            <BaseButton
              color="transparent"
              rounded
              text-color="proxify-primary-700"
              class="p-0 text-body-sm font-semibold"
              align="left"
              padding="none"
              data-testid="open-report-button-completed"
              @click="handleOpenReport"
            >
              <BaseIcon
                name="link-external-01"
                class="ml-1 w-5 h-5"
              />
              Open report
            </BaseButton>
          </template>
          <template v-else>
            <BaseButton
              color="transparent"
              rounded
              text-color="proxify-gray-300"
              class="p-0 text-body-sm font-semibold !opacity-100"
              align="left"
              padding="none"
              disable
              data-testid="open-report-button-not-completed"
            >
              <BaseIcon
                name="link-external-01"
                class="ml-1 w-5 h-5"
              />
              Open report
            </BaseButton>
            <BaseButton
              class="text-proxify-primary font-semibold p-0"
              icon-prepend="copy-03"
              icon-size="20px"
              padding="0"
              @click="handleCopyLink"
            >
              Copy link
            </BaseButton>
          </template>
        </template>
      </div>
    </template>
  </ApplicantCard>
</template>

<style scoped>
.task-card {
  @apply grid w-56 max-h-96 p-2;

  grid-template-rows: 1fr auto;
  break-inside: avoid;
}
</style>
